import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { formatHours, formatSeconds } from "@/lib/format";
import { useCompleteTask, useSelfCompleteTask, type MyTask } from "@/hooks/use-my-tasks";
import { useTimer } from "@/contexts/TimerContext";
import { EditableDeadline } from "./EditableDeadline";
import TimeTrackingPopup from "@/components/time-registration/TimeTrackingPopup";
import { TaskComments } from "@/components/project/TaskComments";
import { useTaskCommentCounts } from "@/hooks/use-task-comments";
import { useHighlight } from "@/hooks/use-highlight";
import type { ProjectCommentUnread } from "@/hooks/use-project-comment-reads";
import { ProjectGroupUpdatesIndicator } from "./ProjectGroupUpdatesIndicator";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Collapsed-by-default mobile card for a task.
 * Tap to expand and reveal primary actions (start timer, log time, send for approval).
 */
export default function TaskMobileCard({
  task,
  showProject,
  userId,
  onNavigate,
  unread,
  onOpenUpdates,
}: {
  task: MyTask;
  showProject: boolean;
  userId: string | null;
  onNavigate?: (projectId: string) => void;
  /** VIB-2180: ulest-oppsummering for prosjektets oppdateringer (kun med showProject). */
  unread?: ProjectCommentUnread;
  /** VIB-2180: åpner ProjectUpdatesDialog. Udefinert = flagg av, ingen ikon. */
  onOpenUpdates?: (projectId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTimePopup, setShowTimePopup] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);
  const { data: commentCounts } = useTaskCommentCounts([task.id]);
  const commentCount = commentCounts?.get(task.id) ?? 0;
  const highlightRef = useHighlight<HTMLDivElement>([task.id, task.budget_line_id]);

  const { activeTimer, elapsedSeconds, startTimer, stopTimer } = useTimer();
  const isTaskActive = activeTimer?.taskId === task.id;
  const isProjectLocked = task.project_status === "completed";

  const timerDisplay = isTaskActive
    ? formatSeconds(elapsedSeconds)
    : formatHours(task.logged_hours);

  const handleTimerToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProjectLocked) return;
    if (isTaskActive) {
      await stopTimer();
    } else {
      await startTimer({
        taskId: task.id,
        projectId: task.project_id,
        projectName: task.project_name,
        taskDescription: task.task_description,
        phase: task.phase,
        budgetLineId: task.budget_line_id || null,
        assignmentId: task.id,  // VIB-671: task.id is the assignment.id in MyTask
      });
    }
  };

  return (
    <div ref={highlightRef} className="border-b border-border/50 last:border-0">
      {/* VIB-2180: prosjektnavn ligger utenfor header-knappen så oppdaterings-
          ikonet (egen <button>) ikke nestes i en <button>. Mobil-lista er flat,
          derfor per kort og kun badge (compact), ikke «Du er nevnt»-teksten. */}
      {showProject && (
        <div className="flex items-center justify-between gap-2 px-4 pt-3">
          <p className="text-sm font-medium truncate">{task.project_name}</p>
          {onOpenUpdates && (
            <ProjectGroupUpdatesIndicator
              compact
              unread={unread}
              onOpen={() => onOpenUpdates(task.project_id)}
              className="-my-2"
            />
          )}
        </div>
      )}
      {/* Collapsed header — large tap target */}
      <button
        className={cn("w-full flex items-center gap-3 px-4 py-4 text-left", showProject && "pt-2")}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">
                {task.phase} — {task.task_description}
              </p>
              {task.notes && (
                <p className="text-[11px] text-muted-foreground italic truncate">{task.notes}</p>
              )}
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <EditableDeadline task={task} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {commentCount > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-primary text-primary-foreground tabular-nums"
              title={`${commentCount} kommentar${commentCount === 1 ? "" : "er"}`}
            >
              {commentCount}
            </span>
          )}
          <div className="text-right">
            <p
              className={cn(
                "font-mono text-sm tabular-nums",
                isTaskActive && "text-blue-600 font-semibold"
              )}
            >
              {timerDisplay}
            </p>
            <p className="text-[10px] text-muted-foreground">
              / {formatHours(task.assigned_hours)}
            </p>
          </div>
          {expanded ? (
            <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground"  />
          ) : (
            <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground"  />
          )}
        </div>
      </button>

      {/* Expanded actions */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-border/30 pt-3">
          <button
            onClick={handleTimerToggle}
            disabled={isProjectLocked}
            aria-label={isTaskActive ? "Stopp timer" : "Start timer"}
            title={isProjectLocked ? "Prosjektet er lukket — åpne det på nytt for å logge timer" : undefined}
            className={cn(
              "w-full h-12 flex items-center justify-center gap-2 text-sm font-medium transition-colors border-[1.5px]",
              isTaskActive
                ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                : "bg-card text-foreground border-foreground hover:bg-muted",
              isProjectLocked && "opacity-40 cursor-not-allowed",
            )}
          >
            <SrIcon name={isTaskActive ? "pause" : "play"} className="w-5 h-5" />
            {isTaskActive ? `Stopp timer (${timerDisplay})` : "Start timer"}
          </button>
          <button
            ref={timeAnchorRef}
            onClick={() => setShowTimePopup(!showTimePopup)}
            className="w-full h-12 flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
          >
            Logg tid
          </button>
          {showTimePopup && (
            <TimeTrackingPopup
              task={task}
              onClose={() => setShowTimePopup(false)}
              userId={userId}
              assignmentId={task.id}
              anchorRef={timeAnchorRef}
            />
          )}

          <MobileStatusAction task={task} />

          {showProject && (
            <button
              onClick={() => onNavigate?.(task.project_id)}
              className="w-full h-10 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <SrIcon name="external-link" className="w-3.5 h-3.5"  />
              Åpne prosjekt
            </button>
          )}

          <TaskComments assignmentId={task.id} />
        </div>
      )}
    </div>
  );
}

/** Full-width "Send til godkjenning"-knapp med confirmation */
function MobileStatusAction({ task }: { task: MyTask }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const completeMut = useCompleteTask();
  const selfCompleteMut = useSelfCompleteTask();
  // VIB-956: PL markerer egne oppgaver «Fullført» rett til godkjent.
  const isPL = !!task.is_project_lead;

  // Sendbar uten loggede timer (Erik-beslutning 2026-06-02): tildelte timer
  // teller i faktureringsgrad ved godkjenning.
  const isCandidate =
    task.task_status === "in_progress" || task.task_status === "not_started";
  if (!isCandidate) return null;

  const handleConfirm = () => {
    if (isPL) selfCompleteMut.mutate(task.id);
    else completeMut.mutate(task.id);
    setShowConfirm(false);
  };

  const pending = completeMut.isPending || selfCompleteMut.isPending;

  return (
    <>
      <AlertDialog open={showConfirm} onOpenChange={(open) => !open && setShowConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isPL ? "Marker som fullført?" : "Send til godkjenning?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isPL ? (
                <>
                  Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) markeres
                  som fullført og godkjent. Som prosjektleder trenger du ikke separat godkjenning.
                </>
              ) : (
                <>
                  Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) sendes
                  til leder for godkjenning. Du har logget{" "}
                  {formatHours(task.logged_hours)} av {formatHours(task.assigned_hours)} tildelt.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Bekreft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <button
        onClick={() => setShowConfirm(true)}
        disabled={pending}
        className="w-full h-12 flex items-center justify-center gap-2 border-[1.5px] border-primary bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
      >
        <SrIcon name="check" className="w-4 h-4" /> {isPL ? "Fullfør" : "Send til godkjenning"}
      </button>
    </>
  );
}
